import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Campaign as CampaignIcon,
  Notifications as NotificationsIcon,
  Public as PublicIcon,
} from "@mui/icons-material";
import axios from "axios";
import { Player, SPL_DIVISIONS, PADEL_EAGLE_DIVISIONS } from "../types";
import { useOrganization } from "../contexts/OrganizationContext";
import PopupEditorPanel from "../components/PopupEditorPanel";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const getOrganizationId = (): string => {
  const currentOrgStr = localStorage.getItem('currentOrganization');
  if (currentOrgStr) {
    try {
      const currentOrg = JSON.parse(currentOrgStr);
      return currentOrg.id || 'spl';
    } catch (e) {}
  }
  return 'spl';
};

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  'X-Organization-Id': getOrganizationId(),
});

interface AnnouncementFormData {
  message: string;
  isEnabled: boolean;
  backgroundColor: string;
  linkUrl?: string;
  linkLabel?: string;
}

const Announcements: React.FC = () => {
  const { currentOrg } = useOrganization();
  const [currentTab, setCurrentTab] = useState(0);

  // â”€â”€ Banner state â”€â”€
  const [announcements, setAnnouncements] = useState<{
    public: AnnouncementFormData;
    playerDashboard: AnnouncementFormData;
    login: AnnouncementFormData;
  }>({
    public: { message: "", isEnabled: false, backgroundColor: "#1976d2" },
    playerDashboard: { message: "", isEnabled: false, backgroundColor: "#2e7d32" },
    login: { message: "", isEnabled: false, backgroundColor: "#d32f2f" },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Popup targeting data shared with PopupEditorPanel
  const [players, setPlayers] = useState<Player[]>([]);

  const divisionOptions: string[] = currentOrg?.divisions?.length
    ? currentOrg.divisions
    : currentOrg?.slug === "padel-eagle"
      ? PADEL_EAGLE_DIVISIONS
      : SPL_DIVISIONS;

  // â”€â”€ Shared â”€â”€
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => { fetchAnnouncements(); }, []);
  useEffect(() => { if (currentTab === 3) { fetchPlayersList(); } }, [currentTab]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const [publicRes, playerRes, loginRes] = await Promise.all([
        axios.get(`${API_URL}/api/announcements/settings/public`, { headers }),
        axios.get(`${API_URL}/api/announcements/settings/player-dashboard`, { headers }),
        axios.get(`${API_URL}/api/announcements/settings/login`, { headers }),
      ]);
      setAnnouncements({
        public: {
          message: publicRes.data.message || "",
          isEnabled: publicRes.data.isEnabled || false,
          backgroundColor: publicRes.data.backgroundColor || "#1976d2",
          linkUrl: publicRes.data.linkUrl || "",
          linkLabel: publicRes.data.linkLabel || "Open link",
        },
        playerDashboard: {
          message: playerRes.data.message || "",
          isEnabled: playerRes.data.isEnabled || false,
          backgroundColor: playerRes.data.backgroundColor || "#2e7d32",
          linkUrl: playerRes.data.linkUrl || "",
          linkLabel: playerRes.data.linkLabel || "Open link",
        },
        login: {
          message: loginRes.data.message || "",
          isEnabled: loginRes.data.isEnabled || false,
          backgroundColor: loginRes.data.backgroundColor || "#d32f2f",
          linkUrl: loginRes.data.linkUrl || "",
          linkLabel: loginRes.data.linkLabel || "Open link",
        },
      });
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load announcement settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayersList = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/players`, { headers: getAuthHeaders() });
      setPlayers(res.data);
    } catch (err) {
      console.error("Failed to load players for targeting:", err);
    }
  };

  const handleSave = async (location: 'public' | 'playerDashboard' | 'login') => {
    try {
      setSaving(true); setError(""); setSuccess("");
      const data = announcements[location];
      if (data.isEnabled && !data.message.trim()) { setError("Message cannot be empty when enabled"); setSaving(false); return; }
      const endpoint = location === 'playerDashboard' ? 'player-dashboard' : location;
      await axios.put(`${API_URL}/api/announcements/settings/${endpoint}`,
        {
          message: data.message,
          isEnabled: data.isEnabled,
          backgroundColor: data.backgroundColor,
          linkUrl: data.linkUrl || "",
          linkLabel: data.linkLabel || "Open link",
        },
        { headers: getAuthHeaders() });
      setSuccess("Announcement updated successfully!");
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update announcement");
    } finally { setSaving(false); }
  };

  const updateAnnouncement = (location: 'public' | 'playerDashboard' | 'login', field: keyof AnnouncementFormData, value: any) => {
    setAnnouncements(prev => ({ ...prev, [location]: { ...prev[location], [field]: value } }));
  };

  const getCurrentLocation = (): 'public' | 'playerDashboard' | 'login' =>
    currentTab === 0 ? 'public' : currentTab === 1 ? 'playerDashboard' : 'login';

  if (loading) {
    return <Container maxWidth="md" sx={{ mt: 4, display: "flex", justifyContent: "center" }}><CircularProgress /></Container>;
  }

  const currentData = announcements[getCurrentLocation()];

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <CampaignIcon sx={{ fontSize: 40, color: "primary.main" }} />
        <Typography variant="h4" component="h1">Announcements</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={(_, v) => setCurrentTab(v)} variant="fullWidth" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Public Homepage" />
          <Tab label="Player Dashboard" />
          <Tab label="Login Page" />
          <Tab label="Player Pop-ups" icon={<NotificationsIcon fontSize="small" />} iconPosition="start" />
          <Tab label="Public Pop-ups" icon={<PublicIcon fontSize="small" />} iconPosition="start" />
        </Tabs>

        {/* â”€â”€ Banner tabs 0-2 â”€â”€ */}
        {currentTab < 3 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {currentTab === 0 && "Public Homepage Announcement"}
              {currentTab === 1 && "Player Dashboard Announcement"}
              {currentTab === 2 && "Login Page Announcement"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {currentTab === 0 && "Displayed as a scrolling marquee on the public homepage."}
              {currentTab === 1 && "Displayed on the player dashboard for logged-in players."}
              {currentTab === 2 && "Displayed on the login page."}
            </Typography>

            <FormControlLabel
              sx={{ mb: 2 }}
              control={<Switch checked={currentData.isEnabled} onChange={(e) => updateAnnouncement(getCurrentLocation(), 'isEnabled', e.target.checked)} />}
              label={<Typography variant="body1">{currentData.isEnabled ? "Enabled" : "Disabled"}</Typography>}
            />

            <TextField
              label="Announcement Message" value={currentData.message}
              onChange={(e) => updateAnnouncement(getCurrentLocation(), 'message', e.target.value)}
              fullWidth multiline rows={3} placeholder="Enter your announcement here..."
              disabled={!currentData.isEnabled} sx={{ mb: 3 }}
              helperText={`${currentData.message.length} characters`}
            />

            <TextField
              label="Button Link URL (optional)"
              value={currentData.linkUrl || ""}
              onChange={(e) => updateAnnouncement(getCurrentLocation(), 'linkUrl', e.target.value)}
              fullWidth
              placeholder="/view-tournaments or https://example.com"
              disabled={!currentData.isEnabled}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Button Label"
              value={currentData.linkLabel || "Open link"}
              onChange={(e) => updateAnnouncement(getCurrentLocation(), 'linkLabel', e.target.value)}
              fullWidth
              disabled={!currentData.isEnabled}
              sx={{ mb: 3 }}
              helperText="Shown only when a link URL is provided"
            />

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>Background Color</Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <input type="color" value={currentData.backgroundColor}
                  onChange={(e) => updateAnnouncement(getCurrentLocation(), 'backgroundColor', e.target.value)}
                  disabled={!currentData.isEnabled}
                  style={{ width: "60px", height: "40px", border: "1px solid #ccc", borderRadius: "4px", cursor: currentData.isEnabled ? "pointer" : "not-allowed" }} />
                <TextField value={currentData.backgroundColor}
                  onChange={(e) => updateAnnouncement(getCurrentLocation(), 'backgroundColor', e.target.value)}
                  disabled={!currentData.isEnabled} size="small" label="Hex Color" sx={{ width: "150px" }} />
              </Box>
            </Box>

            <Button variant="contained" onClick={() => handleSave(getCurrentLocation())}
              disabled={saving || (currentData.isEnabled && !currentData.message.trim())} fullWidth size="large">
              {saving ? "Saving..." : "Save Announcement"}
            </Button>

            {currentData.isEnabled && currentData.message && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom color="text.secondary">Preview</Typography>
                  <Box sx={{ bgcolor: currentData.backgroundColor, color: "white", p: 2, borderRadius: 1, overflow: "hidden" }}>
                    <Typography sx={{ whiteSpace: "nowrap", animation: "marquee 20s linear infinite",
                      "@keyframes marquee": { "0%": { transform: "translateX(100%)" }, "100%": { transform: "translateX(-100%)" } } }}>
                      ðŸ“¢ {currentData.message}
                    </Typography>
                  </Box>
                  {currentData.linkUrl && (
                    <Box sx={{ mt: 1.5 }}>
                      <Button size="small" variant="contained" disabled>
                        {currentData.linkLabel || "Open link"}
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {/* â”€â”€ Player Pop-ups tab â”€â”€ */}
        {currentTab === 3 && (
          <Box sx={{ p: 3 }}>
            <PopupEditorPanel
              variant="player"
              apiListPath="/api/announcements/popups"
              players={players}
              divisionOptions={divisionOptions}
            />
          </Box>
        )}

        {/* â”€â”€ Public Pop-ups tab â”€â”€ */}
        {currentTab === 4 && (
          <Box sx={{ p: 3 }}>
            <PopupEditorPanel
              variant="public"
              apiListPath="/api/announcements/public-popups"
            />
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Announcements;
